import {
  Badge,
  Button,
  createDataTableColumnHelper,
  DataTable,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Link as UiLink,
  SearchField,
} from "@beweegbaas/ui";
import {
  createFileRoute,
  Link,
  Outlet,
  stripSearchParams,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "@beweegbaas/ui";
import { Page } from "#/components/page";
import { DataTableActions } from "#/components/data-table-actions";
import { FilterButton, type FilterGroup } from "#/components/filter-button";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { getLeerjaarTranslation } from "#/modules/util.i18n/enum-mapping";
import {
  STUDENTS_SEARCH_DEFAULTS,
  studentsSearchSchema,
} from "#/modules/school.students/schema.search";
import {
  getStudentsOverview,
  submitDeleteStudent,
} from "#/modules/school.students/students.functions";
import {
  studentsOverviewQueryOptions,
  STUDENTS_OVERVIEW_QUERY_KEY,
} from "#/modules/school.students/query.students-overview";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/overview",
)({
  validateSearch: studentsSearchSchema,
  // Keeps default filter/sort/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(STUDENTS_SEARCH_DEFAULTS)] },
  // All search keys feed the query, so they belong in the match identity — entering with
  // different filters re-runs the loader (staleTime only skips same-deps re-entries).
  loaderDeps: ({ search }) => search,
  staleTime: Infinity,
  // Primes the same query the component reads; loader data doubles as the render fallback
  // when the query cache entry was GC'd.
  loader: ({ context, params: { school, schoolyear }, deps }) =>
    context.queryClient.ensureQueryData(studentsOverviewQueryOptions({ school, schoolyear }, deps)),
  component: StudentsOverviewPage,
});

type StudentRow = Awaited<ReturnType<typeof getStudentsOverview>>["students"][number];

const helper = createDataTableColumnHelper<StudentRow>();

function StudentsOverviewPage() {
  const { school, schoolyear } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const router = useRouter();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { klasId } = search;
  const overview = useOverviewSearch(search, navigate);

  // Data reads live in TanStack Query: the query key is the typed, match-synced `Route.useSearch()`
  // value, so filter/sort/page changes are served by this query (keepPreviousData → stale rows stay
  // visible + dimmed while the next result fetches), NOT by re-running the route loader. The loader
  // already primed this exact key, so the cache entry carries a truthful `dataUpdatedAt` and goes
  // stale on schedule; `loaderData` is only the render fallback if that entry was GC'd (in which
  // case this query is refetching, and `isListPending` dims the rows).
  const overviewQuery = useQuery({
    ...studentsOverviewQueryOptions({ school, schoolyear }, search),
    placeholderData: keepPreviousData,
  });
  const { students, totalPages, totalCount, klassen } = overviewQuery.data ?? loaderData;

  // During a deps-change transition the mounted query (old key) isn't fetching, so the dim
  // needs the router's pending state too.
  const isNavigating = useRouterState({ select: (s) => s.isLoading });
  const isListPending = overviewQuery.isFetching || isNavigating;

  const klasItems = klassen.map((klas) => ({
    id: klas.id.toString(),
    label: klas.name,
  }));

  const filterGroups: FilterGroup[] = [
    {
      id: "klassen",
      label: "Klassen",
      options: klasItems,
      selectedValue: klasId,
      onValueChange: (value) =>
        navigate({
          search: (prev) => ({ ...prev, klasId: value, page: 1 }),
          replace: true,
          resetScroll: false,
        }),
    },
  ];

  // `Route.useSearch()` is the single source of truth for the header structure decision below and
  // the query key — no separate applied-filters snapshot.
  const hasActiveFilters = overview.hasSearchTerm || Boolean(klasId);
  const showEmptyState = students.length === 0 && !hasActiveFilters;

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("name", {
          header: "Naam",
          enableSorting: true,
          meta: { rowTarget: true },
          cell: ({ row, getValue }) => (
            <UiLink
              variant="secondary"
              underline
              to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
              params={(prev: Record<string, string>) => ({
                ...prev,
                id: row.original.id.toString(),
              })}
            >
              {getValue()}
            </UiLink>
          ),
        }),
        helper.accessor("leerjaar", {
          header: "Leerjaar",
          enableSorting: true,
          cell: ({ getValue }) => (
            <Badge variant="blue" size="sm">
              {getLeerjaarTranslation(getValue())}
            </Badge>
          ),
        }),
        helper.accessor("klasName", {
          header: "Klas",
          enableSorting: true,
          meta: { headerClassName: "w-full" },
        }),
        helper.accessor("studentNumber", {
          header: "Leerlingnummer",
          enableSorting: true,
          meta: { cellClassName: "w-0" },
          cell: ({ getValue }) => (
            <span className="text-muted-foreground font-mono tabular-nums">#{getValue()}</span>
          ),
        }),
        helper.display({
          id: "actions",
          header: "",
          meta: { action: true, headerClassName: "w-0" },
          cell: ({ row }) => (
            <DataTableActions
              actions={[
                {
                  key: "edit",
                  label: m["student.bewerk"](),
                  icon: <IconMaterialSymbolsEdit className="size-4" />,
                  render: (
                    <Link
                      from={Route.fullPath}
                      to="/$locale/{-$schoolyear}/$school/students/overview/$id/modal"
                      params={(prev: Record<string, string>) => ({
                        ...prev,
                        id: row.original.id.toString(),
                      })}
                      search={(prev) => prev}
                    />
                  ),
                },
                {
                  key: "delete",
                  label: m["student.verwijder"](),
                  icon: <IconMaterialSymbolsDeleteOutline className="size-4" />,
                  destructive: true,
                  confirm: {
                    title: m["school.students.overview.delete_confirm_title"](),
                    message: m["school.students.overview.delete_confirm"](),
                  },
                  onClick: async () => {
                    await submitDeleteStudent({ data: { leerlingId: row.original.id } });
                    toast.success(m["school.students.overview.delete_success"]());
                    // List data lives in TanStack Query — invalidate its keys so the active
                    // useQuery refetches; router.invalidate() re-runs the loader (first-paint
                    // snapshot + any sibling route data).
                    await Promise.all([
                      queryClient.invalidateQueries({
                        queryKey: [{ school, schoolyear }, STUDENTS_OVERVIEW_QUERY_KEY],
                      }),
                      router.invalidate(),
                    ]);
                  },
                },
              ]}
            />
          ),
        }),
      ]),
    [queryClient, router, school, schoolyear],
  );

  return (
    <Page>
      {!showEmptyState && (
        <Page.Header>
          <div className="flex items-center gap-2">
            <SearchField
              className="w-auto grow md:w-xs"
              placeholder="Zoek op naam of leerlingnummer"
              {...overview.searchFieldProps}
            />
            <FilterButton filters={filterGroups} />
          </div>
          <Page.HeaderActions>
            <Page.Header.MainAction>
              <Button
                className="max-sm:w-full max-sm:grow"
                variant="default"
                size="xl"
                render={
                  <Link
                    from={Route.fullPath}
                    to="/$locale/{-$schoolyear}/$school/students/overview/modal"
                    params={(prev: Record<string, string>) => prev}
                    search={(prev) => prev}
                  />
                }
                nativeButton={false}
              >
                Voeg een leerling toe
                <IconMaterialSymbolsAdd />
              </Button>
            </Page.Header.MainAction>
          </Page.HeaderActions>
        </Page.Header>
      )}
      {showEmptyState && <Page.MobileHeader />}

      {showEmptyState ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{m["school.students.overview.empty"]()}</EmptyTitle>
            <EmptyDescription>
              Importeer leerlingen om klassen aan te maken en hier een overzicht te zien.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              variant="default"
              size="xl"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/detail/import/$type"
                  params={(prev: Record<string, string>) => ({
                    ...prev,
                    type: "modify-existing-year",
                  })}
                />
              }
              nativeButton={false}
            >
              {m["common.empty_state.import_students"]()}
              <IconMaterialSymbolsArrowRightAltRounded />
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <PendingContent pending={isListPending}>
          <DataTable
            alignToContainer
            aria-label="Leerlingen"
            columns={columns}
            data={students}
            getRowId={(row) => row.id.toString()}
            empty={m["school.students.overview.empty"]()}
            {...overview.sortProps}
          />
          <Page.Container>
            <Pager
              totalPages={totalPages}
              totalCount={totalCount}
              className="border-0"
              {...overview.pagerProps}
            />
          </Page.Container>
        </PendingContent>
      )}

      <Outlet />
    </Page>
  );
}
